/**
 * The section library — **thirty-eight** block types, and nothing else.
 *
 * ADR-0013 §4's nine are here; FR-I14's thirteen are in `library.tsx`, C17's
 * three event sections (ADR-0036 §2) in `events.tsx` and C21's thirteen
 * (ADR-0043 §2) in `library-two.tsx`, all re-exported from this module, so
 * `site/blocks` stays the one door. What they share — the
 * context, the link rule, the section wrapper, the reserved image box, the
 * anchor — is in `common.tsx`.
 *
 * **Thirty-six of them are a reader and a renderer** (ADR-0020). The reader turns a
 * block's props into a view-model and is the only place a rule such as FR-I4 is
 * applied; the renderer turns the view-model into markup. A template may supply
 * its own renderers (`Template.sections`), and a renderer never sees the props —
 * so a template cannot show a price the reader has already removed, nor a
 * testimonial a conduct switch suppressed (ADR-0022 §6). `rich_text` and
 * `image` have no view-model: their content *is* their markup.
 *
 * Each is a pure function of `(props, context)` and each may return `null`.
 * **A `null` view renders nothing at all** — not an empty heading, not a bare
 * band — because an empty box on a firm's public page is worse than no
 * section: the firm reads it as something it has not filled in, and a visitor
 * reads it as a site that is broken.
 *
 * `renderBlock` is the only entry: an unrecognised `type` is **skipped
 * silently**, because a firm's site rendering all but one of its sections is a
 * defect a person can see and report, and a firm's site returning a 500
 * because the service learned a twenty-third block type first is an outage on
 * somebody's business address. That asymmetry is why the library can grow from
 * nine to twenty-two without a flag day.
 */

import { cloneElement, Fragment, isValidElement, type ReactElement, type ReactNode } from 'react'

import { iconKey, SiteIcon } from '../icons'
import { href, list, num, obj, rupees, src, str } from '../read'
import type { Block, PostView } from '../types'
import {
  Buttons,
  CardImage,
  formatDate,
  readAnchor,
  readButtons,
  Section,
  siteHref,
  type SiteButton,
  type BlockContext,
  type ContactDetails,
  type SectionAnchor,
} from './common'
import { EnquiryForm, enquiryProps } from './enquiry-form'
import * as events from './events'
import * as library from './library'
import * as libraryTwo from './library-two'
import { hasRichText, RichTextBody } from './rich'

export {
  Buttons,
  CardImage,
  formatDate,
  Section,
  siteHref,
  link,
  action,
  readAnchor,
  readButtons,
  type SiteButton,
  type BlockContext,
  type ContactDetails,
  type SectionAnchor,
} from './common'
export * from './library'
export * from './events'
export * from './library-two'

/** One of `hero.slides` (ADR-0036 §4): a picture and what a screen reader says of it. */
export interface HeroSlide {
  image: string
  alt: string | null
}

export interface HeroView {
  heading: string | null
  subheading: string | null
  body: string | null
  /**
   * The hero's picture: `image_url`, or — C17, ADR-0036 §4 — the first slide
   * when `image_url` is empty, so a firm that leaves Encore keeps a picture on
   * every other theme.
   */
  image: string | null
  /** C17: every slide, in order, for a theme that crossfades them (Encore). */
  slides: HeroSlide[]
  /**
   * C20, ADR-0041 §1 — the firm's own buttons, resolved: zero, one or two,
   * primary then secondary. **There is no automatic *Client login* any more**;
   * the header and footer carry the portal's entry (C18).
   */
  buttons: SiteButton[]
  alignment: 'left' | 'centre'
}

export function readHero(props: Record<string, unknown>, ctx: BlockContext): HeroView | null {
  const heading = str(props.heading)
  const subheading = str(props.subheading)
  if (!heading && !subheading) return null
  const slides = list(props.slides)
    .map((raw) => {
      const slide = obj(raw)
      const image = src(slide.image_url)
      return image ? { image, alt: str(slide.image_alt) } : null
    })
    .filter((s): s is HeroSlide => s !== null)
    .slice(0, 5)
  return {
    heading,
    subheading,
    // Wave 3 close §6.2. Both were entered nowhere until this wave's editor,
    // and both were dropped on the floor when they were: `body` is the
    // paragraph under the lede — the sample content has carried one since 3C —
    // and `alignment` is one of the two the schema allows.
    body: str(props.body),
    image: src(props.image_url) ?? slides[0]?.image ?? null,
    slides,
    buttons: readButtons(props.buttons, ctx),
    alignment: props.alignment === 'centre' ? 'centre' : 'left',
  }
}

/**
 * The hero's buttons — **the firm's, and only the firm's** (C20, ADR-0041 §1).
 * Shared by all nine themes, which is why the automatic *Client login* that
 * used to sit here reached every hero, and why removing it here removed it from
 * every one. An empty list draws nothing.
 */
export function HeroActions({ view }: { view: Pick<HeroView, 'buttons'> }) {
  return <Buttons className="hero-actions" buttons={view.buttons} />
}

function Hero(view: HeroView): ReactNode {
  const { heading, subheading, body, image, alignment } = view
  return (
    <section className={`block hero hero-${alignment}${image ? ' hero-image' : ''}`}>
      <div className="wrap hero-inner">
        <div className="hero-copy">
          {heading ? <h1>{heading}</h1> : null}
          {subheading ? <p className="lede">{subheading}</p> : null}
          {body ? <p className="hero-body">{body}</p> : null}
          <HeroActions view={view} />
        </div>
        {image ? (
          <div className="hero-art">
            <img src={image} alt="" width={720} height={480} loading="eager" decoding="async" />
          </div>
        ) : null}
      </div>
    </section>
  )
}

/**
 * Wave 3 close §6.2: `image_url` and `highlights` were both ignored.
 *
 * The image sits in a **fixed 4:3 box** rather than at its own dimensions, and
 * that is §6.1's answer for this block: an `<img>` with no width and height
 * has no box until its bytes arrive, so the prose beside it jumps the moment
 * they do. `object-fit: cover` inside a reserved box crops rather than
 * distorts, and it needs no dimensions from the firm — which is why the
 * `image` block, where the picture *is* the content and cropping it would be
 * wrong, takes the two numbers instead.
 */
export interface AboutView {
  heading: string | null
  /** The rich-text value, for `RichTextBody` — already known to be non-empty. */
  body: unknown
  image: string | null
  highlights: { label: string; value: string }[]
}

export function readAbout(props: Record<string, unknown>): AboutView | null {
  if (!hasRichText(props.body)) return null
  const highlights = list(props.highlights)
    .map((raw) => {
      const item = obj(raw)
      const label = str(item.label)
      const value = str(item.value)
      return label && value ? { label, value } : null
    })
    .filter((h): h is NonNullable<typeof h> => h !== null)
  return { heading: str(props.heading), body: props.body, image: src(props.image_url), highlights }
}

function About(view: AboutView): ReactNode {
  const { image, highlights } = view
  return (
    <Section className={`block about${image ? ' about-image' : ''}`} heading={view.heading}>
      <div className="about-inner">
        <div className="prose">
          <RichTextBody value={view.body} />
        </div>
        {image ? (
          <div className="about-art">
            <img src={image} alt="" width={640} height={480} loading="lazy" decoding="async" />
          </div>
        ) : null}
      </div>
      {highlights.length > 0 ? (
        <dl className="highlights">
          {highlights.map((h, i) => (
            <div className="highlight" key={i}>
              <dt>{h.value}</dt>
              <dd>{h.label}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </Section>
  )
}

/**
 * **The catalog, as the service resolved it** — `_resolve_services_block` reads
 * `prodesk_services` at render, so a firm that renames a service has renamed it
 * on its website. The names are the service's: `name`, `description`, and a
 * `price` that is an object, not a string.
 *
 * Wave 3D read `title`, `summary` and a scalar `price`, against a fake it wrote
 * itself; the Wave 3 close ran it against the real payload and the whole
 * services section — the most load-bearing block a practice's site has —
 * rendered nothing at all. The 3D names stay as fallbacks (w3d note §5.1).
 */
export interface ServiceItem {
  title: string
  summary: string | null
  link: string | null
  /** The service's picture (FR-I13), from the catalogue's `image_url`. */
  image: string | null
  /** Formatted, or `null` — and `null` whenever FR-I4 hides prices. */
  price: string | null
  priceNote: string | null
  /** C23, ADR-0045 §4: the catalogue's `icon`, the firm's pick, or `null`. */
  icon: string | null
  /**
   * C25, ADR-0054 §5 (waves.md Wave 27 I-6): the service's category, as the
   * render payload carries it, or `null` where the payload names none.
   */
  category: ServiceCategoryRef | null
}

/** I-6 — a services item's category: the catalogue's slug, name and order. */
export interface ServiceCategoryRef {
  slug: string
  name: string
  order: number
}

export interface ServicesView {
  heading: string | null
  intro: string | null
  layout: 'grid' | 'list'
  items: ServiceItem[]
  /**
   * C25, ADR-0054 §5 — the section's *Group by category*, off by default. The
   * service has already put the items in the firm's category order; a theme
   * asks `serviceGroups` for the groups and never sorts.
   */
  groupByCategory: boolean
}

/**
 * One category's run of services in a grouped Services section (I-6).
 *
 * `start` is the place of the group's first service in the **whole section**,
 * from `0`: a theme that numbers its services numbers `start + i`, so the
 * numbers run on across the headings rather than starting again under each.
 */
export interface ServiceGroup {
  /** The category's name. `''` only for services whose category the payload
   *  did not name — drawn last, with no heading, and never dropped. */
  heading: string
  items: ServiceItem[]
  start: number
}

/**
 * **The groups a Services section draws, or `null` to draw it as it always
 * was** — C25, ADR-0054 §5, waves.md Wave 27 I-6.
 *
 * `null` when grouping is off, and also when no service carries a category —
 * there is nothing to group by, and the section is drawn exactly as an
 * ungrouped one. A category's services are gathered under its first
 * appearance, which on a real payload is a run (the service sorted them), and
 * nothing is ever left out: grouping reorders a section and never empties one
 * (ADR-0049).
 *
 * Every theme draws the groups **inside its one `id="services"`** — never a
 * section per category, which would repeat the id the theme contract forbids
 * twice and give each category a section heading of its own (ADR-0054,
 * *Alternatives*).
 */
export function serviceGroups(view: ServicesView): ServiceGroup[] | null {
  if (!view.groupByCategory || view.items.length === 0) return null
  if (!view.items.some((item) => item.category)) return null
  const order: string[] = []
  const byKey = new Map<string, ServiceItem[]>()
  const heading = new Map<string, string>()
  for (const item of view.items) {
    // The uncategorised are one run, after every named category.
    const key = item.category ? `c:${item.category.slug}` : ''
    if (!byKey.has(key)) {
      byKey.set(key, [])
      heading.set(key, item.category?.name ?? '')
      if (key) order.push(key)
    }
    byKey.get(key)?.push(item)
  }
  if (byKey.has('')) order.push('')
  let start = 0
  return order.map((key) => {
    const items = byKey.get(key) ?? []
    const group = { heading: heading.get(key) ?? '', items, start }
    start += items.length
    return group
  })
}

/** A payload item's `category`, or `null` unless it names a slug and a name. */
function readCategory(value: unknown): ServiceCategoryRef | null {
  const category = obj(value)
  const slug = str(category.slug)
  const name = str(category.name)
  if (!slug || !name) return null
  return { slug, name, order: typeof category.order === 'number' && Number.isFinite(category.order) ? category.order : 0 }
}

export function readServices(props: Record<string, unknown>, ctx: BlockContext): ServicesView | null {
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const title = str(item.name) ?? str(item.title)
      if (!title) return null
      // `price` is `{amount, currency, billing_cadence, gst_treatment}` or
      // absent. Absent means the policy hid it or the plan has no fee, and the
      // two are deliberately the same on the page (`price_view`).
      const price = obj(item.price)
      return {
        title,
        summary: str(item.description) ?? str(item.summary),
        link: siteHref(href(item.href), ctx),
        image: src(item.image_url),
        icon: iconKey(item.icon),
        // FR-I4, enforced here and not in a template: a template that forgot
        // would advertise a fee its regulator forbids.
        price: ctx.showPricing ? rupees(price.amount ?? item.price) : null,
        priceNote: ctx.showPricing
          ? (str(price.billing_cadence)?.replace(/_/g, ' ') ?? str(item.price_note))
          : null,
        category: readCategory(item.category),
      }
    })
    .filter((i): i is NonNullable<typeof i> => i !== null)
  if (items.length === 0) return null
  return {
    heading: str(props.heading),
    intro: str(props.intro),
    // Wave 3 close §6.2. `layout` is `grid` or `list` in the schema and the
    // default is `grid`, which is what the existing CSS already draws.
    layout: props.layout === 'list' ? 'list' : 'grid',
    items,
    groupByCategory: props.group_by_category === true,
  }
}

function Services(view: ServicesView): ReactNode {
  const { items, layout, intro } = view
  const groups = serviceGroups(view)
  return (
    <Section id="services" className={`block services services-${layout}`} heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      {groups ? (
        <div className="service-groups">
          {groups.map((group) => (
            <div className="service-group" key={group.start}>
              {group.heading ? <h3 className="service-group-heading">{group.heading}</h3> : null}
              {serviceCards(group.items)}
            </div>
          ))}
        </div>
      ) : (
        serviceCards(items)
      )}
    </Section>
  )
}

/** The shared renderer's cards — one list per group when grouped, one in all when not. */
function serviceCards(items: ServiceItem[]): ReactNode {
  return (
    <ul className="cards">
      {items.map((item, i) => (
        <li className="card" key={i}>
          {item.image ? <CardImage src={item.image} /> : null}
          <SiteIcon name={item.icon} className="card-icon" />
          <h3>{item.link ? <a href={item.link}>{item.title}</a> : item.title}</h3>
          {item.summary ? <p>{item.summary}</p> : null}
          {item.price ? (
            <p className="price">
              {item.price}
              {item.priceNote ? <span className="price-note"> {item.priceNote}</span> : null}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

export interface TeamMember {
  name: string
  role: string | null
  bio: string | null
  photo: string | null
}

export interface TeamView {
  heading: string | null
  intro: string | null
  members: TeamMember[]
}

export function readTeam(props: Record<string, unknown>): TeamView | null {
  const members = list(props.members)
    .map((raw) => {
      const item = obj(raw)
      const name = str(item.name)
      if (!name) return null
      return { name, role: str(item.role), bio: str(item.bio), photo: src(item.photo_url) }
    })
    .filter((m): m is NonNullable<typeof m> => m !== null)
  if (members.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), members }
}

function Team(view: TeamView): ReactNode {
  const { members, intro } = view
  return (
    <Section id="team" className="block team" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ul className="cards">
        {members.map((m, i) => (
          <li className="card person" key={i}>
            {m.photo ? (
              <img className="portrait" src={m.photo} alt={m.name} width={160} height={160} loading="lazy" />
            ) : null}
            <h3>{m.name}</h3>
            {m.role ? <p className="role">{m.role}</p> : null}
            {m.bio ? <p>{m.bio}</p> : null}
          </li>
        ))}
      </ul>
    </Section>
  )
}

/**
 * How to reach the firm.
 *
 * **The block's own fields win over the site's** — `use_site_contact` is a
 * schema field and `_resolve_contact_block` on the service has already folded
 * the firm's details into the block's props when it is true. So this reads the
 * props first and the site's details only as a fallback, which is the order
 * the service resolved them in; reading the site alone (3D) meant a `contact`
 * block deliberately carrying a *different* address — a branch office, a
 * registered office — rendered the head office anyway.
 *
 * Wave 3 close §6.2 and §6.3 both land here: `intro` and `map_url` were
 * ignored, and `whatsapp` and `hours` were unreadable because the type named
 * `city`, `state` and `pincode` — three keys `validate_contact` refuses — in
 * place of the three the service actually stores.
 */
export interface ContactView {
  heading: string | null
  intro: string | null
  address: string | null
  email: string | null
  phone: string | null
  whatsapp: string | null
  hours: string | null
  mapUrl: string | null
  note: string | null
  enquiry: ReturnType<typeof enquiryProps>
}

export function readContact(
  props: Record<string, unknown>,
  ctx: BlockContext,
  site: ContactDetails,
  index = 0,
): ContactView | null {
  const address = str(props.address) ?? site.address
  const email = str(props.email) ?? site.email
  const phone = str(props.phone) ?? site.phone
  const whatsapp = site.whatsapp
  const hours = site.hours ?? str(props.timings)
  const mapUrl = href(props.map_url) ?? href(site.map_url)
  const intro = str(props.intro)
  const note = str(props.note)
  // w4e — FR-I6. Read before the emptiness check below, because a block with
  // no address and no telephone but a working enquiry form is a block worth
  // rendering: the form is how the visitor reaches the firm. `email` is the
  // props-first one resolved above, so the form's failure notice offers the
  // address this block actually shows.
  // T-4.28: `index` makes this form's field ids unique, and `noticesAtIndex`
  // decides whether it is the one that draws the three `:target` notices — a
  // page may now carry a `lead_form` beside this block.
  const enquiry = enquiryProps(props, ctx.enquiryAction, ctx.pagePath, email, {
    idPrefix: `enq${index}`,
    notices: ctx.noticesAtIndex === undefined || ctx.noticesAtIndex === index,
  })
  if (!address && !email && !phone && !whatsapp && !hours && !intro && !note && !enquiry)
    return null
  return { heading: str(props.heading), intro, address, email, phone, whatsapp, hours, mapUrl, note, enquiry }
}

function Contact(view: ContactView): ReactNode {
  const { intro, address, email, phone, whatsapp, hours, mapUrl, note, enquiry } = view
  return (
    <Section id="contact" className="block contact" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <div className="contact-grid">
        {address ? (
          <address>
            {address.split(/\n+/).map((line, i) => (
              <span key={i}>{line}</span>
            ))}
            {mapUrl ? (
              <a className="map-link" href={mapUrl} rel="noopener noreferrer">
                Find us on a map
              </a>
            ) : null}
          </address>
        ) : null}
        <ul className="contact-list">
          {email ? (
            <li>
              <a href={`mailto:${email}`}>{email}</a>
            </li>
          ) : null}
          {phone ? (
            <li>
              <a href={`tel:${phone.replace(/[^+\d]/g, '')}`}>{phone}</a>
            </li>
          ) : null}
          {/* A number, not a `wa.me` link. `wa.me` is a third party, and a
              public page on a firm's own domain does not hand a visitor to one
              (ADR-0013 §4). A client can dial it or copy it. */}
          {whatsapp ? <li>WhatsApp {whatsapp}</li> : null}
          {hours ? <li>{hours}</li> : null}
        </ul>
      </div>
      {note ? <p className="note">{note}</p> : null}
      {/* The seam Wave 3D left here, filled — T-4.8, FR-I6. The form posts to
          the service and makes a lead carrying the service asked about and the
          page it came from. It renders **only** when there is somewhere to post
          to (`enquiryProps` returns null otherwise), which keeps 3D's rule
          intact: this block still claims nothing it cannot do. */}
      {enquiry ? <EnquiryForm {...enquiry} /> : null}
    </Section>
  )
}

export interface FaqView {
  heading: string | null
  items: { question: string; answer: string }[]
}

export function readFaq(props: Record<string, unknown>): FaqView | null {
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const question = str(item.question)
      const answer = str(item.answer)
      return question && answer ? { question, answer } : null
    })
    .filter((i): i is NonNullable<typeof i> => i !== null)
  if (items.length === 0) return null
  return { heading: str(props.heading), items }
}

function Faq(view: FaqView): ReactNode {
  const { items } = view
  return (
    <Section className="block faq" heading={view.heading}>
      <dl className="faq-list">
        {items.map((item, i) => (
          <div className="faq-item" key={i}>
            <dt>{item.question}</dt>
            <dd>{item.answer}</dd>
          </div>
        ))}
      </dl>
    </Section>
  )
}

/**
 * **The names here are the service's, not this file's** — the Wave 3 close's
 * reconciliation (w3d note §5.1). 3C's `_resolve_blog_block` resolves the posts
 * at render and puts them in `items`, dated `published_on`, summarised
 * `summary`, with the `href` already built. Wave 3D guessed `posts`/`date`/
 * `excerpt` against a fake, which rendered an empty section for a firm that had
 * published — the one failure mode this block has, and a silent one.
 *
 * The guessed names are kept as fallbacks rather than deleted: they cost one
 * `??` each and they are what the older fixture carries.
 */
export interface BlogPost {
  title: string
  slug: string
  href: string
  date: string | null
  excerpt: string | null
  author: string | null
  /** The post's cover picture (FR-I13). */
  cover: string | null
}

export interface BlogView {
  heading: string | null
  posts: BlogPost[]
  /**
   * **Present only on the blog page** — C24 item 15, ADR-0050 §2. Where it is,
   * this block is the index: the chips, twenty posts, the search box and a
   * *Load more* that is a link. Where it is not, it is the short list it has
   * always been, cut to the firm's own `count`.
   */
  index?: {
    topics: { name: string; slug: string }[]
    topic: string | null
    query: string | null
    page: number
    hasMore: boolean
    nextPage: number | null
    previousPage: number | null
    total: number
  }
}

export function readBlog(props: Record<string, unknown>, ctx: BlockContext): BlogView | null {
  // Wave 3 close §6.2. Both are already applied by `_resolve_blog_block` on
  // the service — it slices to `count` and nulls the summary when
  // `show_summaries` is false — so they are read here as a **second** guard
  // and not as the first one: a payload rendered from a fixture, from a
  // sample, or by a future caller that resolves the posts differently must
  // honour what the firm asked for. Reading them costs two lines; not reading
  // them means the behaviour lives in exactly one place and is untestable
  // from this side.
  const limit = num(props.count) ?? 12
  const showSummaries = props.show_summaries !== false
  // **C24 item 15 (ADR-0050 §2): on the blog page this block is the index.**
  //
  // `ctx.blogIndex` is present only on a render of `/blog`, and only there
  // does the block draw the chips, twenty posts, the search box and *Load
  // more*. On a home page it is the same short list it has always been, with
  // the firm's own `count` — which is what keeps *the three latest posts on
  // the front page* working while the blog page becomes a real index.
  const index = ctx.blogIndex ?? null
  const posts = list(index ? index.posts : (props.items ?? props.posts))
    .map((raw) => {
      const item = obj(raw)
      const title = str(item.title)
      const slug = str(item.slug)
      if (!title || !slug) return null
      return {
        title,
        slug,
        href: siteHref(str(item.href) ?? `/blog/${slug}`, ctx) as string,
        date: str(item.published_on) ?? str(item.date),
        excerpt: showSummaries ? (str(item.summary) ?? str(item.excerpt)) : null,
        author: str(item.author),
        cover: src(item.cover_image_url),
      }
    })
    .filter((p): p is NonNullable<typeof p> => p !== null)
    // The index has already been paged by the service; `count` is the firm's
    // limit for a short list and must not cut a page of twenty to twelve.
    .slice(0, index ? Number.MAX_SAFE_INTEGER : limit)
  // **An index with no posts still draws**, where a short list does not: a
  // firm whose search found nothing needs to see the box it searched in and
  // the chips it filtered by, or it has no way back.
  if (posts.length === 0 && !index) return null
  return {
    heading: str(props.heading),
    posts,
    ...(index
      ? {
          index: {
            topics: index.topics ?? [],
            topic: index.topic ?? null,
            query: index.query ?? null,
            page: index.page ?? 1,
            hasMore: index.has_more === true,
            nextPage: index.next_page ?? null,
            previousPage: index.previous_page ?? null,
            total: index.total ?? posts.length,
          },
        }
      : {}),
  }
}

/**
 * **The blog list, and on the blog page the whole index** — C24 item 15,
 * ADR-0050 §2.
 *
 * Where `view.index` is set the block draws the chips, the search box, the
 * posts and a *Load more*; where it is not, the short list it has always been.
 *
 * **No script, anywhere in it** (ADR-0013 §2):
 *
 * - the chips are `<a>`s to `/blog?topic=<slug>`, so each is a real address a
 *   visitor can bookmark and a crawler can follow;
 * - the search is a `GET` form with one field, which the browser submits on
 *   its own;
 * - *Load more* is an `<a>` to `?page=2`, not a button that fetches — so each
 *   page is cacheable on its own and the thing works with no JavaScript at
 *   all, which is the whole posture of a published page here.
 *
 * `rel="prev"` and `rel="next"` are on the two links, so the pages read as one
 * series rather than as a hundred near-identical pages.
 */
function BlogList(view: BlogView): ReactNode {
  const { posts, index } = view
  /** `/blog` with the filter and the page the link means, and nothing else. */
  const href = (over: { topic?: string | null; q?: string | null; page?: number | null }) => {
    const params = new URLSearchParams()
    const topic = over.topic === undefined ? index?.topic : over.topic
    const q = over.q === undefined ? index?.query : over.q
    if (topic) params.set('topic', topic)
    if (q) params.set('q', q)
    if (over.page && over.page > 1) params.set('page', String(over.page))
    const query = params.toString()
    return query ? `/blog?${query}` : '/blog'
  }
  return (
    <Section className="block blog-list" heading={view.heading}>
      {index ? (
        <>
          {index.topics.length > 0 ? (
            <nav className="blog-topics" aria-label="Topics">
              <a
                className={index.topic ? 'blog-topic' : 'blog-topic blog-topic-on'}
                href={href({ topic: null, page: null })}
                {...(index.topic ? {} : { 'aria-current': 'page' as const })}
              >
                All
              </a>
              {index.topics.map((topic) => (
                <a
                  key={topic.slug}
                  className={
                    topic.slug === index.topic ? 'blog-topic blog-topic-on' : 'blog-topic'
                  }
                  href={href({ topic: topic.slug, page: null })}
                  {...(topic.slug === index.topic ? { 'aria-current': 'page' as const } : {})}
                >
                  {topic.name}
                </a>
              ))}
            </nav>
          ) : null}
          <form className="blog-search" method="get" action="/blog" role="search">
            {/* The filter survives a search, because losing it on every
                search is how a visitor ends up back at the start. */}
            {index.topic ? <input type="hidden" name="topic" value={index.topic} /> : null}
            <label className="blog-search-label" htmlFor="blog-q">
              Search the blog
            </label>
            <input
              id="blog-q"
              className="blog-search-input"
              type="search"
              name="q"
              defaultValue={index.query ?? ''}
              placeholder="A word in the title"
            />
            <button className="button small blog-search-go" type="submit">
              Search
            </button>
          </form>
        </>
      ) : null}
      {index && posts.length === 0 ? (
        <p className="blog-empty">
          Nothing matches that yet.{' '}
          <a href={href({ topic: null, q: null, page: null })}>See every post</a>.
        </p>
      ) : null}
      <ul className="posts">
        {posts.map((post, i) => (
          <li key={i}>
            {post.cover ? <CardImage src={post.cover} /> : null}
            <h3>
              <a href={post.href}>{post.title}</a>
            </h3>
            <p className="byline">
              {post.date ? <time dateTime={post.date}>{formatDate(post.date)}</time> : null}
              {post.date && post.author ? ' \u00b7 ' : null}
              {post.author}
            </p>
            {post.excerpt ? <p>{post.excerpt}</p> : null}
          </li>
        ))}
      </ul>
      {index && (index.previousPage || index.hasMore) ? (
        <nav className="blog-pages" aria-label="More posts">
          {index.previousPage ? (
            <a className="button small" rel="prev" href={href({ page: index.previousPage })}>
              Newer posts
            </a>
          ) : null}
          {index.hasMore && index.nextPage ? (
            <a className="button small" rel="next" href={href({ page: index.nextPage })}>
              Load more
            </a>
          ) : null}
        </nav>
      ) : null}
    </Section>
  )
}

/**
 * One blog post, at `/blog/<slug>` — the page every `BlogList` item links to.
 *
 * Not a block, and not one of ADR-0013's nine: a post is not placed on a page
 * by the firm, it *is* the page, addressed by its own slug. So it is drawn
 * here, beside the list that links to it, from the same helpers — the byline
 * is `BlogList`'s byline and the body is a `rich_text` block's body — and
 * `render.tsx` puts it inside the firm's chrome exactly as it does a page.
 *
 * `blogHref` is the firm's blog page when it has one, for the way back; `null`
 * when the firm has no page with the slug `blog`, in which case there is no
 * link rather than one to a 404.
 */
export function PostArticle({
  post,
  blogHref,
  ctx,
}: {
  post: PostView
  blogHref: string | null
  ctx: BlockContext
}): ReactNode {
  const date = str(post.published_on)
  const author = str(post.author)
  const cover = src(post.cover_image_url)
  const back = siteHref(blogHref, ctx)
  return (
    <article className="block post">
      <div className="wrap prose">
        {back ? (
          <p className="post-back">
            <a href={back}>← All posts</a>
          </p>
        ) : null}
        <h1>{post.title}</h1>
        {date || author ? (
          <p className="byline">
            {date ? <time dateTime={date}>{formatDate(date)}</time> : null}
            {date && author ? ' · ' : null}
            {author}
          </p>
        ) : null}
        {cover ? (
          <figure className="post-cover">
            <img src={cover} alt="" width={1200} height={630} loading="eager" decoding="async" />
          </figure>
        ) : null}
        {hasRichText(post.body) ? <RichTextBody value={post.body} /> : null}
      </div>
    </article>
  )
}

function RichTextBlock(props: Record<string, unknown>): ReactNode {
  if (!hasRichText(props.body)) return null
  return (
    <Section className="block rich-text" heading={str(props.heading)}>
      <div className="prose">
        <RichTextBody value={props.body} />
      </div>
    </Section>
  )
}

/**
 * One picture.
 *
 * **The Wave 3 close's §6.1.** An `<img>` with no `width` and `height` has no
 * box until its bytes arrive: the browser lays the page out around a
 * zero-height element and then re-lays it out when the image decodes, so
 * everything below jumps — on a phone, on a slow connection, mid-sentence.
 * The base stylesheet's `img{max-width:100%;height:auto}` is the other half:
 * with both attributes present a browser computes `aspect-ratio` from them, so
 * the reserved box stays proportional as the column narrows. **The CSS was
 * already right** — what was missing was the two attributes.
 *
 * The two numbers are the image's own, captured by the builder at the moment
 * the firm chooses the picture (`routes/Site/fields/ImageField.tsx`) and stored
 * on the block — `BLOCK_SCHEMAS['image']`'s `width_px` and `height_px`. They
 * are optional, because every image block written before this wave has
 * neither: without them the image still renders, exactly as badly as it did
 * before, and the fix reaches a page the next time the firm opens it.
 *
 * `image_url` is the service's name for it, and this file read `src` until the
 * Wave 3 close — so an image block rendered nothing at all. `src` stays as a
 * fallback for the same reason as `BlogList`'s.
 */
function ImageBlock(props: Record<string, unknown>): ReactNode {
  const source = src(props.image_url ?? props.src)
  if (!source) return null
  const caption = str(props.caption)
  const width = num(props.width_px)
  const height = num(props.height_px)
  const box = width && height ? { width, height } : {}
  const size = props.width === 'full' || props.width === 'inline' ? String(props.width) : 'wide'
  return (
    <section className="block image">
      <div className="wrap">
        <figure className={`figure-${size}`}>
          <img
            src={source}
            alt={str(props.alt) ?? ''}
            {...box}
            loading="lazy"
            decoding="async"
          />
          {caption ? <figcaption>{caption}</figcaption> : null}
        </figure>
      </div>
    </section>
  )
}

/**
 * Every block that has a view-model, and its view-model's type.
 *
 * **Thirty-six of the thirty-eight** since C21: `rich_text` and `image` have no
 * view-model, because their content *is* their markup and there is no rule to
 * apply between the two.
 */
export interface BlockViews {
  hero: HeroView
  about: AboutView
  services: ServicesView
  team: TeamView
  contact: ContactView
  faq: FaqView
  blog_list: BlogView
  // The section library (FR-I14, ADR-0022 §7). A template that names none of
  // these still draws all thirteen, with the shared renderers below.
  page_hero: library.PageHeroView
  showcase: library.ShowcaseView
  metrics: library.MetricsView
  split_feature: library.SplitFeatureView
  product_spotlight: library.SpotlightView
  feature_grid: library.FeatureGridView
  logo_strip: library.LogoStripView
  testimonial_strip: library.TestimonialView
  statement: library.StatementView
  steps: library.StepsView
  story_strip: library.StoryStripView
  lead_form: library.LeadFormView
  cta: library.CtaView
  // C17's three (ADR-0036 §2, I-1). The eight light themes draw them with the
  // shared renderers below (§3); Encore draws its own.
  countdown: events.CountdownView
  schedule: events.ScheduleView
  tickets: events.TicketsView
  // C21's thirteen (ADR-0043 §2, waves.md Wave 21 I-1). The nine existing
  // themes draw them with the shared renderers below (§4); a new theme redraws
  // the eight generic ones and its own family's verticals.
  pricing: libraryTwo.PricingView
  tabs: libraryTwo.TabsView
  timeline: libraryTwo.TimelineView
  video: libraryTwo.VideoView
  before_after: libraryTwo.BeforeAfterView
  marquee: libraryTwo.MarqueeView
  comparison: libraryTwo.ComparisonView
  hours: libraryTwo.HoursView
  price_list: libraryTwo.PriceListView
  courses: libraryTwo.CoursesView
  openings: libraryTwo.OpeningsView
  goal: libraryTwo.GoalView
  product_grid: libraryTwo.ProductGridView
}

/**
 * A template's own renderers (ADR-0020), keyed by block type. Any type it does
 * not name renders with the shared one below.
 *
 * The view a renderer receives carries the section's `anchor` too (I-3), for a
 * renderer that wants to know it — **the id itself is set by `renderBlock`**,
 * and a renderer must not set its own id on the section's root.
 */
export type SectionRenderers = {
  [K in keyof BlockViews]?: (view: BlockViews[K] & SectionAnchor, ctx: BlockContext) => ReactNode
}

const SHARED: { [K in keyof BlockViews]: (view: BlockViews[K], ctx: BlockContext) => ReactNode } = {
  hero: Hero,
  about: About,
  services: Services,
  team: Team,
  contact: Contact,
  faq: Faq,
  blog_list: BlogList,
  page_hero: library.PageHero,
  showcase: library.Showcase,
  metrics: library.Metrics,
  split_feature: library.SplitFeature,
  product_spotlight: library.Spotlight,
  feature_grid: library.FeatureGrid,
  logo_strip: library.LogoStrip,
  testimonial_strip: library.Testimonials,
  statement: library.Statement,
  steps: library.Steps,
  story_strip: library.StoryStrip,
  lead_form: library.LeadForm,
  cta: library.CtaBand,
  countdown: events.Countdown,
  schedule: events.Schedule,
  tickets: events.Tickets,
  pricing: libraryTwo.Pricing,
  tabs: libraryTwo.Tabs,
  timeline: libraryTwo.Timeline,
  video: libraryTwo.Video,
  before_after: libraryTwo.BeforeAfter,
  marquee: libraryTwo.Marquee,
  comparison: libraryTwo.Comparison,
  hours: libraryTwo.Hours,
  price_list: libraryTwo.PriceList,
  courses: libraryTwo.Courses,
  openings: libraryTwo.Openings,
  goal: libraryTwo.Goal,
  product_grid: libraryTwo.ProductGrid,
}

/** The template's renderer for a type, or the shared one. A null view — a
 *  block with nothing in it, or a section the firm's policy suppresses —
 *  renders nothing on every template alike. */
function draw<K extends keyof BlockViews>(
  type: K,
  view: BlockViews[K] | null,
  ctx: BlockContext,
  anchor: string | null = null,
): ReactNode {
  if (view === null) return null
  const own = ctx.sections?.[type] as ((v: BlockViews[K] & SectionAnchor, c: BlockContext) => ReactNode) | undefined
  const shared = SHARED[type] as (v: BlockViews[K] & SectionAnchor, c: BlockContext) => ReactNode
  const node = (own ?? shared)(anchor ? { ...view, anchor } : view, ctx)
  return anchor ? withId(node, anchor) : node
}

/**
 * **The section's id is its anchor** (ADR-0036 §7.1, I-3), set here on the
 * root every renderer returns so no renderer — shared or any theme's — has to
 * remember. Replacing, not adding: the fixed `services` / `team` / `contact`
 * ids yield, which is what keeps an anchor from colliding with anything.
 *
 * A renderer's root is an element (`<section>`), the shared `Section`, or a
 * small component a theme composed its band from; a component is called to
 * reach the element it draws — every renderer here is a pure function with no
 * hooks, which is what makes that safe — to at most four levels. Anything else
 * (a fragment, a list) is wrapped in a `<div>` carrying the id, which is a
 * worse shape and still a reachable address.
 */
function withId(node: ReactNode, id: string, depth = 0): ReactNode {
  if (!isValidElement(node)) return node
  const element = node as ReactElement<Record<string, unknown>>
  if (typeof element.type === 'string' || element.type === Section) {
    return cloneElement(element, { id })
  }
  if (typeof element.type === 'function' && element.type !== Fragment && depth < 4) {
    const component = element.type as (props: Record<string, unknown>) => ReactNode
    return withId(component(element.props), id, depth + 1)
  }
  return <div id={id}>{node}</div>
}

/**
 * The one entry. `contact` is the only block that reads the site's own details
 * rather than its props, because a firm's address belongs to the firm and not
 * to a block somebody could delete.
 *
 * `index` is the block's place on the page, and it is load-bearing for
 * `lead_form`: it makes the form's field ids unique and it decides which form
 * draws the `:target` notices when a page carries two (T-4.28).
 */
/**
 * **The view a block reads to, or `null` when nothing would be drawn** — C24
 * item 11, [ADR-0049](../../../../docs/adr/0049-a-section-with-content-is-drawn-and-the-builder-says-when-it-is-not.md) §2.
 *
 * This switch *is* the renderer's reading. `renderBlock` hands its answer to
 * `draw`, which draws nothing for `null`; `blocks/visibility.ts: whyHidden`
 * asks the same question and tells the builder why.
 *
 * **One expression, two readers**, and that is the whole point of it being
 * exported. The owner's finding was that a firm could add a section, fill it
 * in, and find it absent from the preview with nothing saying so (*"in some
 * pages componants/section able to add but it not get visible in preview"*).
 * A builder that answered from its own copy of these rules would agree on the
 * day it was written and drift the first time a reader changed.
 *
 * `rich_text` and `image` are not here: they have no view model and are drawn
 * straight from their props. `whyHidden` reads them itself and says so.
 */
export function blockView(
  type: string | undefined,
  props: Record<string, unknown>,
  ctx: BlockContext,
  contact: ContactDetails,
  index = 0,
): unknown {
  switch (type) {
    case 'hero':
      return readHero(props, ctx)
    case 'about':
      return readAbout(props)
    case 'services':
      return readServices(props, ctx)
    case 'team':
      return readTeam(props)
    case 'contact':
      return readContact(props, ctx, contact, index)
    case 'faq':
      return readFaq(props)
    case 'blog_list':
      return readBlog(props, ctx)
    // ── the section library, FR-I14 ──────────────────────────────────
    case 'page_hero':
      return library.readPageHero(props, ctx)
    case 'showcase':
      return library.readShowcase(props, ctx, index)
    case 'metrics':
      return library.readMetrics(props)
    case 'split_feature':
      return library.readSplitFeature(props, ctx)
    case 'product_spotlight':
      return library.readSpotlight(props, ctx)
    case 'feature_grid':
      return library.readFeatureGrid(props, ctx)
    case 'logo_strip':
      return library.readLogoStrip(props, ctx)
    case 'testimonial_strip':
      return library.readTestimonials(props, ctx)
    case 'statement':
      return library.readStatement(props)
    case 'steps':
      return library.readSteps(props)
    case 'story_strip':
      return library.readStoryStrip(props, ctx)
    case 'lead_form':
      return library.readLeadForm(props, ctx, index)
    case 'cta':
      return library.readCta(props, ctx)
    // ── the event sections, FR-I17 (C17, ADR-0036 §2) ───────────────────
    case 'countdown':
      return events.readCountdown(props, ctx)
    case 'schedule':
      return events.readSchedule(props, index)
    case 'tickets':
      return events.readTickets(props, ctx)
    // ── the second library, FR-I19 (C21, ADR-0043 §2) ───────────────────
    case 'pricing':
      return libraryTwo.readPricing(props, ctx)
    case 'tabs':
      return libraryTwo.readTabs(props, ctx, index)
    case 'timeline':
      return libraryTwo.readTimeline(props, ctx)
    case 'video':
      return libraryTwo.readVideo(props)
    case 'before_after':
      return libraryTwo.readBeforeAfter(props)
    case 'marquee':
      return libraryTwo.readMarquee(props)
    case 'comparison':
      return libraryTwo.readComparison(props, ctx)
    case 'hours':
      return libraryTwo.readHours(props, ctx)
    case 'price_list':
      return libraryTwo.readPriceList(props, ctx)
    case 'courses':
      return libraryTwo.readCourses(props, ctx)
    case 'openings':
      return libraryTwo.readOpenings(props, ctx)
    case 'goal':
      return libraryTwo.readGoal(props, ctx)
    case 'product_grid':
      return libraryTwo.readProductGrid(props, ctx)
    default:
      // Unknown type — and `rich_text` and `image`, which have no view.
      // Skipped, deliberately and silently; see the file header.
      return null
  }
}

export function renderBlock(block: Block, index: number, ctx: BlockContext, contact: ContactDetails): ReactNode {
  const props = obj(block?.props)
  const key = `${block?.type ?? 'unknown'}-${index}`
  // C17 — the section's address on its page, set as its id (I-3).
  const anchor = readAnchor(props)
  // The two with no view model, drawn straight from their props.
  if (block?.type === 'rich_text') {
    return <Fragment key={key}>{anchor ? withId(RichTextBlock(props), anchor) : RichTextBlock(props)}</Fragment>
  }
  if (block?.type === 'image') {
    return <Fragment key={key}>{anchor ? withId(ImageBlock(props), anchor) : ImageBlock(props)}</Fragment>
  }
  const view = blockView(block?.type, props, ctx, contact, index)
  if (view === null || view === undefined) return null
  // `draw` looks the renderer up by type — the theme's own, else the shared
  // one — so the dispatch is the key and nothing here repeats it.
  return (
    <Fragment key={key}>{draw(block.type as keyof BlockViews, view as never, ctx, anchor)}</Fragment>
  )
}
